import * as api from '@gitbook/api-client';
import { METER_PRICES, type PricingFeatureKey } from '@gitbook/app-pricing';
import { Icon } from '@gitbook/spine-icons';
import {
    Alert,
    Button,
    Dialog,
    DialogBody,
    DialogBrandedLayout,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    Heading,
    toast,
    useDialogClose,
} from '@gitbook/spine-react';

import { apiClient, useUpgrade } from '../../state';
import { PrettyDollarNumber } from './PrettyDollarNumber';

const ADDON_TO_PRICING_FEATURE_KEY: Record<api.BillingMeter, PricingFeatureKey> = {
    [api.BillingMeter.TranslationWords]: 'translations',
    [api.BillingMeter.AiResponses]: 'ai-lens', // unused for now, to be changed later
    [api.BillingMeter.AiAgentResponses]: 'ai-lens', // unused for now, to be changed later
};

type EnableBillingUsageAddonDialogProps = {
    organization: api.Organization;
    meterName: api.BillingMeter;
    onConfirm: () => void;
};

/**
 * Dialog to enable a billing usage add-on.
 */
export function EnableBillingUsageAddonDialog(props: EnableBillingUsageAddonDialogProps) {
    return (
        <Dialog size={{ width: 832 }}>
            <EnableBillingUsageAddonDialogContent {...props} />
        </Dialog>
    );
}

function EnableBillingUsageAddonDialogContent(props: EnableBillingUsageAddonDialogProps) {
    const { organization, meterName, onConfirm } = props;
    const meterPrice = METER_PRICES[meterName];
    const closeDialog = useDialogClose();

    const upgrade = useUpgrade(ADDON_TO_PRICING_FEATURE_KEY[meterName]);
    const isFreeTrial = organization.trial?.status === api.BillingTrialStatus.Active;
    const canAdminBilling = organization.permissions.admin;

    const onSubmit = async () => {
        await toast.catch(async () => {
            await apiClient.billing.enableOrganizationBillingMeterAddon(organization.id, meterName);
        }, 'Error while enabling usage addon');

        onConfirm();
        closeDialog();
    };

    return (
        <DialogBrandedLayout
            aside={{
                content: (
                    <div>
                        <div>
                            <div className="flex flex-row items-center gap-2">
                                <PrettyDollarNumber
                                    value={meterPrice.flatInitialPrice}
                                    className="text-base text-ui-heading-medium"
                                />
                                <span className="text-secondary text-ui-base">/ month</span>
                            </div>
                        </div>
                        <div className="text-secondary text-ui-small">
                            {meterPrice.startingAt.toLocaleString()}{' '}
                            {meterPrice.unitLabel.toLowerCase()} per month
                        </div>
                        <div className="text-secondary text-ui-small">
                            + <PrettyDollarNumber value={meterPrice.pricePerBatch} /> per extra{' '}
                            {meterPrice.unitsPerBatch.toLocaleString()}{' '}
                            {meterPrice.unitLabel.toLowerCase()}
                        </div>
                    </div>
                ),
            }}
        >
            <DialogHeader>
                <Heading>
                    Enable{' '}
                    {meterPrice.addon?.title ? `${meterPrice.addon?.title} add-on` : 'add-on'}
                </Heading>
            </DialogHeader>

            <DialogBody>
                <div className="flex flex-col gap-3">
                    {meterPrice.addon?.description ? (
                        <p className="text-secondary text-ui-base">
                            {meterPrice.addon.description}
                        </p>
                    ) : null}
                    {meterPrice.addon && meterPrice.addon.sellingPoints.length > 0 ? (
                        <ul className="flex flex-col gap-1">
                            {meterPrice.addon.sellingPoints.map((point) => (
                                <li key={point} className="flex flex-row items-start gap-2">
                                    <div className="flex h-4 w-4 items-center justify-center rounded-full bg-upgrade">
                                        <Icon.Check className="size-3 text-upgrade" />
                                    </div>
                                    <div className="flex-1 text-secondary text-ui-base">
                                        {point}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    ) : null}
                    <p className="text-secondary text-ui-base">
                        Enabling this add-on will add it to your billing subscription.
                    </p>
                    {canAdminBilling ? (
                        <>
                            {isFreeTrial ? (
                                <Alert
                                    kind="info"
                                    size="xsmall"
                                    details="Try this add-on for free during your trial. Once your trial ends, you can add it to your subscription."
                                />
                            ) : null}
                            {upgrade ? (
                                <Alert
                                    kind="upgrade"
                                    size="xsmall"
                                    details={`Before you can enable this add-on: ${upgrade.tooltip?.label}`}
                                    action={{
                                        label: upgrade.label,
                                        onPress: upgrade.onPress,
                                    }}
                                />
                            ) : null}
                        </>
                    ) : (
                        <Alert
                            kind="warning"
                            size="xsmall"
                            details="You don't have permission to enable add-ons. Contact an administrator to enable this add-on."
                        />
                    )}
                </div>
            </DialogBody>
            {upgrade ? null : (
                <DialogFooter>
                    <DialogCloseButton />
                    <Button
                        onPress={onSubmit}
                        kind="upgrade"
                        leadingIcon={Icon.Upgrade}
                        label={isFreeTrial ? 'Try this add-on for free' : 'Enable add-on'}
                        disabled={!!upgrade || !canAdminBilling}
                    />
                </DialogFooter>
            )}
        </DialogBrandedLayout>
    );
}
